import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  tournamentApi,
  TournamentDetails,
  Wrestler,
  AddWrestlerInput,
  EditWrestlerInput,
} from '../services/api-client';
import { useToast } from '../contexts/ToastContext';
import { useAuthContext } from '../contexts/AuthContext';
import { MobileLayout } from '../components/layout/MobileLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PullToRefresh } from '../components/layout/PullToRefresh';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { formatDate, formatDateTimeLocal, getUserTimezone } from '../utils/dateTime';

export default function TournamentDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = useAuthContext();
  const [tournament, setTournament] = useState<TournamentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editingWrestlers, setEditingWrestlers] = useState<
    Record<string, { place: string; teamPoints: string }>
  >({});
  const [savingWrestlers, setSavingWrestlers] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingWrestler, setAddingWrestler] = useState(false);
  const [newWrestler, setNewWrestler] = useState<AddWrestlerInput>({
    name: '',
    weightClass: '',
    seed: undefined,
    schoolTeam: '',
    year: '',
    previousPlacement: '',
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWrestler, setEditingWrestler] = useState<Wrestler | null>(null);
  const [editWrestlerData, setEditWrestlerData] = useState<EditWrestlerInput>({});
  const [updatingWrestler, setUpdatingWrestler] = useState(false);
  const [adRefreshKey, setAdRefreshKey] = useState(0);
  const [showEditTournamentModal, setShowEditTournamentModal] = useState(false);
  const [editTournamentData, setEditTournamentData] = useState({
    numberOfPlaces: '',
    defaultBudget: '',
    wrestlestatUrl: '',
    floUrl: '',
    defaultRosterLockTime: '',
    hashtags: '',
  });
  const [updatingTournament, setUpdatingTournament] = useState(false);
  const [togglingFinalized, setTogglingFinalized] = useState(false);

  useEffect(() => {
    if (id) {
      loadTournament();
    }
  }, [id]);

  const loadTournament = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await tournamentApi.get(id);
      setTournament(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tournament');
    } finally {
      setLoading(false);
    }
  };

  const getWrestlerCost = (seed: number | null): number => {
    if (!tournament || seed === null) return 0;
    const index = seed - 1;
    if (index < 0 || index >= tournament.placePoints.length) return 0;
    return tournament.placePoints[index];
  };

  const startEditWrestler = (wrestler: Wrestler) => {
    setEditingWrestlers((prev) => ({
      ...prev,
      [wrestler.id]: {
        place: wrestler.place?.toString() || '',
        teamPoints: wrestler.teamPoints.toString(),
      },
    }));
  };

  const updateEditValue = (wrestlerId: string, field: 'place' | 'teamPoints', value: string) => {
    setEditingWrestlers((prev) => ({
      ...prev,
      [wrestlerId]: {
        ...prev[wrestlerId],
        [field]: value,
      },
    }));
  };

  const cancelEdit = (wrestlerId: string) => {
    setEditingWrestlers((prev) => {
      const next = { ...prev };
      delete next[wrestlerId];
      return next;
    });
  };

  const saveWrestlerResults = async (wrestler: Wrestler) => {
    if (!tournament || !id) return;

    const editData = editingWrestlers[wrestler.id];
    if (!editData) return;

    const place = editData.place ? parseInt(editData.place) : undefined;
    const teamPoints = parseFloat(editData.teamPoints);

    if (teamPoints < 0) {
      showToast('Team points must be non-negative', 'error');
      return;
    }

    if (place !== undefined && place < 1) {
      showToast('Place must be a positive number', 'error');
      return;
    }

    // Optimistically update UI
    const oldWrestler = { ...wrestler };
    setTournament((prev) => {
      if (!prev) return prev;
      const updatedWrestlers = prev.wrestlers.map((w) =>
        w.id === wrestler.id ? { ...w, place: place || null, teamPoints } : w
      );
      return {
        ...prev,
        wrestlers: updatedWrestlers,
        wrestlersByWeight: groupWrestlersByWeight(updatedWrestlers),
      };
    });

    // Remove from editing state
    cancelEdit(wrestler.id);

    // Mark as saving
    setSavingWrestlers((prev) => new Set(prev).add(wrestler.id));

    try {
      await tournamentApi.updateWrestlerResults(id, wrestler.id, {
        place,
        teamPoints,
      });
      showToast(`Updated ${wrestler.name}`, 'success');
    } catch (err) {
      // Revert on error
      setTournament((prev) => {
        if (!prev) return prev;
        const updatedWrestlers = prev.wrestlers.map((w) =>
          w.id === oldWrestler.id ? oldWrestler : w
        );
        return {
          ...prev,
          wrestlers: updatedWrestlers,
          wrestlersByWeight: groupWrestlersByWeight(updatedWrestlers),
        };
      });
      const errorMsg = err instanceof Error ? err.message : 'Failed to update wrestler';
      showToast(errorMsg, 'error');
    } finally {
      setSavingWrestlers((prev) => {
        const next = new Set(prev);
        next.delete(wrestler.id);
        return next;
      });
    }
  };

  const groupWrestlersByWeight = (wrestlers: Wrestler[]): Record<string, Wrestler[]> => {
    return wrestlers.reduce(
      (acc, wrestler) => {
        if (!acc[wrestler.weightClass]) {
          acc[wrestler.weightClass] = [];
        }
        acc[wrestler.weightClass].push(wrestler);
        return acc;
      },
      {} as Record<string, Wrestler[]>
    );
  };

  const handleAddWrestler = async () => {
    if (!id || !tournament) return;

    if (!newWrestler.name || !newWrestler.weightClass) {
      showToast('Name and weight class are required', 'error');
      return;
    }

    setAddingWrestler(true);
    try {
      const wrestler = await tournamentApi.addWrestler(id, {
        ...newWrestler,
        seed: newWrestler.seed || undefined,
        schoolTeam: newWrestler.schoolTeam || undefined,
        year: newWrestler.year || undefined,
        previousPlacement: newWrestler.previousPlacement || undefined,
      });

      // Add wrestler to state
      setTournament((prev) => {
        if (!prev) return prev;
        const updatedWrestlers = [...prev.wrestlers, wrestler];
        return {
          ...prev,
          wrestlers: updatedWrestlers,
          wrestlersByWeight: groupWrestlersByWeight(updatedWrestlers),
        };
      });

      showToast(`Added ${wrestler.name}`, 'success');
      setShowAddModal(false);
      setNewWrestler({
        name: '',
        weightClass: '',
        seed: undefined,
        schoolTeam: '',
        year: '',
        previousPlacement: '',
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to add wrestler';
      showToast(errorMsg, 'error');
    } finally {
      setAddingWrestler(false);
    }
  };

  const openEditWrestlerModal = (wrestler: Wrestler) => {
    setEditingWrestler(wrestler);
    setEditWrestlerData({
      name: wrestler.name,
      weightClass: wrestler.weightClass,
      seed: wrestler.seed,
      schoolTeam: wrestler.schoolTeam || '',
      year: wrestler.year || '',
      previousPlacement: wrestler.previousPlacement || '',
    });
    setShowEditModal(true);
  };

  const handleEditWrestler = async () => {
    if (!id || !tournament || !editingWrestler) return;

    if (!editWrestlerData.name || !editWrestlerData.weightClass) {
      showToast('Name and weight class are required', 'error');
      return;
    }

    setUpdatingWrestler(true);
    try {
      const updated = await tournamentApi.editWrestler(id, editingWrestler.id, editWrestlerData);

      // Update wrestler in state
      setTournament((prev) => {
        if (!prev) return prev;
        const updatedWrestlers = prev.wrestlers.map((w) => (w.id === updated.id ? updated : w));
        return {
          ...prev,
          wrestlers: updatedWrestlers,
          wrestlersByWeight: groupWrestlersByWeight(updatedWrestlers),
        };
      });

      showToast(`Updated ${updated.name}`, 'success');
      setShowEditModal(false);
      setEditingWrestler(null);
      setEditWrestlerData({});
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update wrestler';
      showToast(errorMsg, 'error');
    } finally {
      setUpdatingWrestler(false);
    }
  };

  const handleDeleteWrestler = async (wrestler: Wrestler) => {
    if (!id || !tournament) return;

    if (
      !confirm(`Are you sure you want to delete ${wrestler.name}? This action cannot be undone.`)
    ) {
      return;
    }

    try {
      await tournamentApi.deleteWrestler(id, wrestler.id);

      // Remove wrestler from state
      setTournament((prev) => {
        if (!prev) return prev;
        const updatedWrestlers = prev.wrestlers.filter((w) => w.id !== wrestler.id);
        return {
          ...prev,
          wrestlers: updatedWrestlers,
          wrestlersByWeight: groupWrestlersByWeight(updatedWrestlers),
        };
      });

      showToast(`Deleted ${wrestler.name}`, 'success');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete wrestler';
      showToast(errorMsg, 'error');
    }
  };

  const handleToggleFinalized = async () => {
    if (!id || !tournament) return;
    const newValue = !tournament.finalized;
    try {
      setTogglingFinalized(true);
      await tournamentApi.finalize(id, newValue);
      setTournament({
        ...tournament,
        finalized: newValue,
        finalizedAt: newValue ? new Date().toISOString() : null,
      });
      showToast(
        newValue ? 'Tournament marked as final' : 'Tournament unmarked as final',
        'success'
      );
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to update', 'error');
    } finally {
      setTogglingFinalized(false);
    }
  };

  const handleDeleteTournament = async () => {
    if (!id || !tournament) return;

    if (
      !confirm(
        `Are you sure you want to delete "${tournament.name}"? This action cannot be undone and will fail if any leagues reference this tournament.`
      )
    ) {
      return;
    }

    try {
      await tournamentApi.deleteTournament(id);
      showToast(`Deleted tournament "${tournament.name}"`, 'success');
      navigate('/tournaments');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete tournament';
      showToast(errorMsg, 'error');
    }
  };

  const openEditTournamentModal = () => {
    if (!tournament) return;
    console.log('Opening edit modal, tournament.wrestlestatUrl:', tournament.wrestlestatUrl);

    // Format the default roster lock time for datetime-local input
    let formattedLockTime = '';
    if (tournament.defaultRosterLockTime) {
      const lockTime = new Date(tournament.defaultRosterLockTime);
      formattedLockTime = formatDateTimeLocal(lockTime);
    }

    setEditTournamentData({
      numberOfPlaces: tournament.placePoints.length.toString(),
      defaultBudget: tournament.defaultBudget.toString(),
      wrestlestatUrl: tournament.wrestlestatUrl || '',
      floUrl: tournament.floUrl || '',
      defaultRosterLockTime: formattedLockTime,
      hashtags: (tournament.hashtags || []).join(' '),
    });
    setShowEditTournamentModal(true);
  };

  const handleUpdateTournament = async () => {
    if (!id || !tournament) return;

    const numberOfPlaces = parseInt(editTournamentData.numberOfPlaces);
    const defaultBudget = parseInt(editTournamentData.defaultBudget);

    if (isNaN(numberOfPlaces) || numberOfPlaces < 1 || numberOfPlaces > 20) {
      showToast('Number of places must be between 1 and 20', 'error');
      return;
    }

    if (isNaN(defaultBudget) || defaultBudget < 0) {
      showToast('Default budget must be a non-negative number', 'error');
      return;
    }

    try {
      setUpdatingTournament(true);

      // Generate new placePoints array if length changed
      let placePoints = tournament.placePoints;
      if (numberOfPlaces !== tournament.placePoints.length) {
        // Keep existing values where possible, fill new spots with 0
        placePoints = Array.from({ length: numberOfPlaces }, (_, i) =>
          i < tournament.placePoints.length ? tournament.placePoints[i] : 0
        );
      }

      // Convert datetime-local to ISO string if provided
      let defaultRosterLockTime = undefined;
      if (editTournamentData.defaultRosterLockTime) {
        const lockTime = new Date(editTournamentData.defaultRosterLockTime);
        defaultRosterLockTime = lockTime.toISOString();
      }

      // Parse hashtags from space-separated string
      const hashtags = editTournamentData.hashtags
        .split(/\s+/)
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const updated = await tournamentApi.update(id, {
        placePoints,
        defaultBudget,
        wrestlestatUrl: editTournamentData.wrestlestatUrl || null,
        floUrl: editTournamentData.floUrl || null,
        defaultRosterLockTime,
        hashtags,
      });

      // Update local state
      setTournament((prev) =>
        prev
          ? {
              ...prev,
              placePoints: updated.placePoints,
              defaultBudget: updated.defaultBudget,
              wrestlestatUrl: updated.wrestlestatUrl,
              floUrl: updated.floUrl,
              defaultRosterLockTime: updated.defaultRosterLockTime,
              hashtags: updated.hashtags,
            }
          : prev
      );

      showToast('Tournament updated successfully', 'success');
      setShowEditTournamentModal(false);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update tournament';
      showToast(errorMsg, 'error');
    } finally {
      setUpdatingTournament(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading tournament...</div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Tournament not found'}</p>
          <button
            onClick={() => navigate('/tournaments')}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Back to tournaments
          </button>
        </div>
      </div>
    );
  }

  const handleRefresh = async () => {
    await loadTournament();
    setAdRefreshKey((prev) => prev + 1);
  };

  return (
    <MobileLayout>
      <PageHeader
        title={tournament.name}
        subtitle={tournament.location ? `📍 ${tournament.location}` : undefined}
        actions={
          <>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-300 hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50"
              aria-label="Refresh"
            >
              <svg
                className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </>
        }
      />

      <PullToRefresh onRefresh={handleRefresh}>
        <div className="px-4 py-4 space-y-4">
          {/* Finalized Banner */}
          {tournament.finalized && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-green-600 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-medium text-green-900">Results are final</span>
              </div>
            </div>
          )}

          {/* Tournament Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Dates</h3>
              <p className="text-lg font-semibold text-gray-900">
                {formatDate(tournament.startDate)}
                {tournament.endDate && tournament.endDate !== tournament.startDate && (
                  <> - {formatDate(tournament.endDate)}</>
                )}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Default Budget</h3>
              <p className="text-lg font-semibold text-gray-900">{tournament.defaultBudget}</p>
              <p className="text-sm text-gray-600 mt-1">Sum of place points</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Weight Classes</h3>
              <p className="text-lg font-semibold text-gray-900">
                {tournament.weightClasses.length}
              </p>
              <p className="text-sm text-gray-600 mt-1">{tournament.weightClasses.join(', ')}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Default Roster Lock Time</h3>
              <p className="text-lg font-semibold text-gray-900">
                {tournament.defaultRosterLockTime
                  ? new Date(tournament.defaultRosterLockTime).toLocaleString()
                  : 'Not set'}
              </p>
              <p className="text-sm text-gray-600 mt-1">For new leagues</p>
            </div>
          </div>

          {/* Ad Banner - After tournament info, before actions */}
          <div className="py-4 mb-4">
            <AdBanner
              slot={AD_SLOTS.tournamentDetails}
              format="horizontal"
              refreshKey={adRefreshKey}
            />
          </div>

          {/* Action Buttons */}
          <div className="mb-8 flex flex-wrap gap-3">
            <button
              onClick={() =>
                navigate('/leagues/create', { state: { tournamentId: tournament.id } })
              }
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-colors"
            >
              Create League for this Tournament
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={openEditTournamentModal}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  ⚙️ Edit Settings
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  + Add Wrestler
                </button>
                <button
                  onClick={() => {
                    setEditMode(!editMode);
                    if (editMode) {
                      // Cancel all edits when leaving edit mode
                      setEditingWrestlers({});
                    }
                  }}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    editMode
                      ? 'bg-gray-600 text-white hover:bg-gray-700'
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  {editMode ? 'Exit Edit Mode' : '✏️ Edit Results (Admin)'}
                </button>
                <button
                  onClick={handleToggleFinalized}
                  disabled={togglingFinalized}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    tournament.finalized
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  } disabled:opacity-50`}
                >
                  {togglingFinalized
                    ? 'Updating...'
                    : tournament.finalized
                      ? 'Un-finalize Results'
                      : 'Finalize Results'}
                </button>
                <button
                  onClick={handleDeleteTournament}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  🗑️ Delete Tournament
                </button>
              </>
            )}
          </div>

          {(tournament.floUrl || tournament.wrestlestatUrl) && (
            <div className="mb-8 flex flex-wrap gap-3">
              {tournament.floUrl && (
                <a
                  href={tournament.floUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-medium bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-sm transition-all"
                >
                  Follow on FloWrestling
                </a>
              )}
              {tournament.wrestlestatUrl && (
                <a
                  href={tournament.wrestlestatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-sm transition-all"
                >
                  Wrestler Stats
                </a>
              )}
            </div>
          )}

          {/* Wrestlers by Weight Class */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Wrestlers</h2>
            {tournament.weightClasses.map((weightClass) => {
              const wrestlers = tournament.wrestlersByWeight[weightClass] || [];
              return (
                <div key={weightClass} className="bg-white rounded-lg shadow">
                  <div className="p-4 border-b bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">{weightClass} lbs</h3>
                    <p className="text-sm text-gray-600">{wrestlers.length} wrestlers</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Seed
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            School/Team
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Cost
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Place
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Points
                          </th>
                          {editMode && (
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Edit Results
                            </th>
                          )}
                          {isAdmin && !editMode && (
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Manage
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {wrestlers.length === 0 ? (
                          <tr>
                            <td
                              colSpan={editMode ? 7 : isAdmin ? 7 : 6}
                              className="px-4 py-4 text-center text-gray-500"
                            >
                              No wrestlers in this weight class yet
                            </td>
                          </tr>
                        ) : (
                          wrestlers.map((wrestler) => {
                            const isEditing = editingWrestlers[wrestler.id];
                            const isSaving = savingWrestlers.has(wrestler.id);

                            return (
                              <tr key={wrestler.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {wrestler.seed ? `#${wrestler.seed}` : 'Unseeded'}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {wrestler.name}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {wrestler.schoolTeam || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {getWrestlerCost(wrestler.seed)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      min="1"
                                      value={isEditing.place}
                                      onChange={(e) =>
                                        updateEditValue(wrestler.id, 'place', e.target.value)
                                      }
                                      placeholder="Place"
                                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  ) : (
                                    wrestler.place || '-'
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.5"
                                      value={isEditing.teamPoints}
                                      onChange={(e) =>
                                        updateEditValue(wrestler.id, 'teamPoints', e.target.value)
                                      }
                                      placeholder="Points"
                                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  ) : (
                                    wrestler.teamPoints || 0
                                  )}
                                </td>
                                {editMode && (
                                  <td className="px-4 py-3 text-sm">
                                    {isEditing ? (
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => saveWrestlerResults(wrestler)}
                                          disabled={isSaving}
                                          className="px-3 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                                        >
                                          {isSaving ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                          onClick={() => cancelEdit(wrestler.id)}
                                          disabled={isSaving}
                                          className="px-3 py-1 bg-gray-400 text-white rounded text-xs font-medium hover:bg-gray-500 disabled:opacity-50"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => startEditWrestler(wrestler)}
                                        className="px-3 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded text-xs font-medium hover:shadow-lg"
                                      >
                                        Edit
                                      </button>
                                    )}
                                  </td>
                                )}
                                {isAdmin && !editMode && (
                                  <td className="px-4 py-3 text-sm">
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => openEditWrestlerModal(wrestler)}
                                        className="px-3 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded text-xs font-medium hover:shadow-lg"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteWrestler(wrestler)}
                                        className="px-3 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </PullToRefresh>

      {/* Add Wrestler Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Add Wrestler</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newWrestler.name}
                  onChange={(e) => setNewWrestler({ ...newWrestler, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weight Class <span className="text-red-500">*</span>
                </label>
                <select
                  value={newWrestler.weightClass}
                  onChange={(e) => setNewWrestler({ ...newWrestler, weightClass: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select weight class</option>
                  {tournament.weightClasses.map((wc) => (
                    <option key={wc} value={wc}>
                      {wc} lbs
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seed</label>
                <input
                  type="number"
                  min="1"
                  value={newWrestler.seed || ''}
                  onChange={(e) =>
                    setNewWrestler({
                      ...newWrestler,
                      seed: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  placeholder="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School/Team</label>
                <input
                  type="text"
                  value={newWrestler.schoolTeam}
                  onChange={(e) => setNewWrestler({ ...newWrestler, schoolTeam: e.target.value })}
                  placeholder="Penn State"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <input
                  type="text"
                  value={newWrestler.year}
                  onChange={(e) => setNewWrestler({ ...newWrestler, year: e.target.value })}
                  placeholder="Senior"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Previous Placement
                </label>
                <input
                  type="text"
                  value={newWrestler.previousPlacement}
                  onChange={(e) =>
                    setNewWrestler({ ...newWrestler, previousPlacement: e.target.value })
                  }
                  placeholder="3rd at NCAA 2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleAddWrestler}
                disabled={addingWrestler || !newWrestler.name || !newWrestler.weightClass}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingWrestler ? 'Adding...' : 'Add Wrestler'}
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewWrestler({
                    name: '',
                    weightClass: '',
                    seed: undefined,
                    schoolTeam: '',
                    year: '',
                    previousPlacement: '',
                  });
                }}
                disabled={addingWrestler}
                className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Wrestler Modal */}
      {showEditModal && editingWrestler && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Edit Wrestler</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editWrestlerData.name || ''}
                  onChange={(e) =>
                    setEditWrestlerData({ ...editWrestlerData, name: e.target.value })
                  }
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weight Class <span className="text-red-500">*</span>
                </label>
                <select
                  value={editWrestlerData.weightClass || ''}
                  onChange={(e) =>
                    setEditWrestlerData({ ...editWrestlerData, weightClass: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select weight class</option>
                  {tournament.weightClasses.map((wc) => (
                    <option key={wc} value={wc}>
                      {wc} lbs
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seed</label>
                <input
                  type="number"
                  min="1"
                  value={editWrestlerData.seed || ''}
                  onChange={(e) =>
                    setEditWrestlerData({
                      ...editWrestlerData,
                      seed: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  placeholder="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School/Team</label>
                <input
                  type="text"
                  value={editWrestlerData.schoolTeam || ''}
                  onChange={(e) =>
                    setEditWrestlerData({ ...editWrestlerData, schoolTeam: e.target.value })
                  }
                  placeholder="Penn State"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <input
                  type="text"
                  value={editWrestlerData.year || ''}
                  onChange={(e) =>
                    setEditWrestlerData({ ...editWrestlerData, year: e.target.value })
                  }
                  placeholder="Senior"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Previous Placement
                </label>
                <input
                  type="text"
                  value={editWrestlerData.previousPlacement || ''}
                  onChange={(e) =>
                    setEditWrestlerData({ ...editWrestlerData, previousPlacement: e.target.value })
                  }
                  placeholder="3rd at NCAA 2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleEditWrestler}
                disabled={
                  updatingWrestler || !editWrestlerData.name || !editWrestlerData.weightClass
                }
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatingWrestler ? 'Updating...' : 'Update Wrestler'}
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingWrestler(null);
                  setEditWrestlerData({});
                }}
                disabled={updatingWrestler}
                className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tournament Settings Modal */}
      {showEditTournamentModal && tournament && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Edit Tournament Settings</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Places <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={editTournamentData.numberOfPlaces}
                  onChange={(e) =>
                    setEditTournamentData({
                      ...editTournamentData,
                      numberOfPlaces: e.target.value,
                    })
                  }
                  placeholder="8"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Current: {tournament.placePoints.length} places
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Budget <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={editTournamentData.defaultBudget}
                  onChange={(e) =>
                    setEditTournamentData({
                      ...editTournamentData,
                      defaultBudget: e.target.value,
                    })
                  }
                  placeholder="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Current: {tournament.defaultBudget}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Roster Lock Time ({getUserTimezone()})
                </label>
                <input
                  type="datetime-local"
                  value={editTournamentData.defaultRosterLockTime}
                  onChange={(e) =>
                    setEditTournamentData({
                      ...editTournamentData,
                      defaultRosterLockTime: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {tournament.defaultRosterLockTime
                    ? `Current: ${new Date(tournament.defaultRosterLockTime).toLocaleString()}`
                    : 'Not set'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WrestleStat URL (Optional)
                </label>
                <input
                  type="url"
                  value={editTournamentData.wrestlestatUrl}
                  onChange={(e) =>
                    setEditTournamentData({
                      ...editTournamentData,
                      wrestlestatUrl: e.target.value,
                    })
                  }
                  placeholder="https://www.wrestlestat.com/event/95953/participants/wrestlers"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {tournament.wrestlestatUrl ? 'Current URL set' : 'No WrestleStat URL configured'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  FloWrestling URL (Optional)
                </label>
                <input
                  type="url"
                  value={editTournamentData.floUrl}
                  onChange={(e) =>
                    setEditTournamentData({
                      ...editTournamentData,
                      floUrl: e.target.value,
                    })
                  }
                  placeholder="https://www.flowrestling.org/events/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {tournament.floUrl ? 'Current URL set' : 'No FloWrestling URL configured'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hashtags (for social sharing)
                </label>
                <input
                  type="text"
                  value={editTournamentData.hashtags}
                  onChange={(e) =>
                    setEditTournamentData({
                      ...editTournamentData,
                      hashtags: e.target.value,
                    })
                  }
                  placeholder="#BigTenWrestling #NCAAWrestling"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Space-separated hashtags included in social share posts
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Changing the number of places will preserve existing point
                  values where possible. New places will default to 0 points.
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleUpdateTournament}
                disabled={
                  updatingTournament ||
                  !editTournamentData.numberOfPlaces ||
                  !editTournamentData.defaultBudget
                }
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatingTournament ? 'Updating...' : 'Update Settings'}
              </button>
              <button
                onClick={() => setShowEditTournamentModal(false)}
                disabled={updatingTournament}
                className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-500 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileLayout>
  );
}
